import { Head, useForm } from "@inertiajs/react"
import { Button, Flex, Heading, Select, Text, TextField } from "@radix-ui/themes"
import { z } from "zod"

import InputError from "@/components/input-error"
import { MarkdownEditor } from "@/components/markdown-editor"
import ProjectLayout from "@/layouts/project-layout"
import { projectEmailsPath, projectDashboardPath } from "@/routes"
import { ProjectSchema } from "@/schemas"
import LinkButton from "@/components/link-button"

const PropsSchema = z.object({
  project: ProjectSchema,
  email_styles: z.array(
    z.object({
      value: z.string(),
      css: z.string(),
    }),
  ),
})

type Props = z.infer<typeof PropsSchema>

export default function EmailNew(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, email_styles } = validatedProps

  const { data, setData, post, processing, errors } = useForm({
    custom_id: "",
    subject: "",
    body: "",
    style: "basic",
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/projects/${project.slug}/emails`)
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Email Templates", href: projectEmailsPath(project.slug) },
        { title: "New Email Template" },
      ]}
    >
      <Head title="New Email Template" />

      <Flex direction="column" gap="6" p="6">
        <Flex direction="column" gap="1">
          <Heading size="6" weight="medium">
            New Email Template
          </Heading>
          <Text color="gray" size="2">
            Create a new email template for your project
          </Text>
        </Flex>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Custom ID <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                value={data.custom_id}
                onChange={(e) => setData("custom_id", e.target.value.toLowerCase())}
                placeholder="Enter a unique identifier (e.g., welcome-email, onboarding-step-1)..."
                pattern="[a-z0-9_-]+"
                required
              />
              <Text color="gray" size="1" mt="1">
                A unique identifier for this email template. Use only lowercase letters, numbers,
                hyphens, and underscores.
              </Text>
              <InputError message={errors?.custom_id?.[0]} />
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Subject <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                value={data.subject}
                onChange={(e) => setData("subject", e.target.value)}
                placeholder="Enter email subject..."
                required
              />
              <InputError message={errors?.subject?.[0]} />
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Style
              </Text>
              <Select.Root value={data.style} onValueChange={(value) => setData("style", value)}>
                <Select.Trigger placeholder="Select email style" />
                <Select.Content position="popper">
                  {email_styles.map((style) => (
                    <Select.Item key={style.value} value={style.value}>
                      {style.value}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
              <Text color="gray" size="1" mt="1">
                Choose the visual style for this email template.
              </Text>
              <InputError message={errors?.style?.[0]} />
            </div>

            <div>
              <Text as="div" size="3" weight="bold" mb="2">
                Body <span className="text-red-500">*</span>
              </Text>
              <MarkdownEditor
                value={data.body}
                onChange={(value) => setData("body", value)}
                placeholder="Enter email body content (Markdown supported)..."
                projectId={project.slug}
                css={email_styles.find((s) => s.value === data.style)?.css ?? ""}
              />
              <Text color="gray" size="1" mt="1">
                You can use Markdown for formatting. The preview on the right shows how it will
                look.
              </Text>
              <InputError message={errors?.body?.[0]} />
            </div>

            <Flex gap="3" justify="end" mt="4">
              <LinkButton href={projectEmailsPath(project.slug)} variant="soft" color="gray">
                Cancel
              </LinkButton>

              <Button type="submit" disabled={processing}>
                {processing ? "Creating..." : "Create email template"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Flex>
    </ProjectLayout>
  )
}
